import { useEffect, useState } from 'react';
import type { AutomationTarget, Track } from '@/domain/types';
import { command } from '@/commands/registry';
import * as TrackCommands from '@/commands/trackCommands';
import * as EffectCommands from '@/commands/effectCommands';
import { EFFECT_DESCRIPTORS, effectDescriptor } from '@/engine/effects/descriptors';
import { getServices } from '@/state/services';
import { useDaw } from '@/state/store';
import { Meter } from '../common/Meter';
import { formatDb, formatPan } from '../TrackHeaders';

/** The mixer. One strip per track, plus the master, in routing order. */
export function Mixer(): JSX.Element {
  const project = useDaw((s) => s.project);
  const selection = useDaw((s) => s.selection);
  const tracks = project.trackOrder.map((id) => project.tracks[id]).filter(Boolean);
  const master = project.tracks[project.masterTrackId];

  return (
    <div className="mixer">
      {tracks.map((track) => (
        <Channel key={track.id} track={track} selected={selection.trackId === track.id} />
      ))}
      {master && <Channel track={master} selected={selection.trackId === master.id} master />}
    </div>
  );
}

function Channel({ track, selected, master }: { track: Track; selected: boolean; master?: boolean }): JSX.Element {
  const matching = useDaw((s) => s.matchingTrackId === track.id);
  const writing = useDaw((s) => s.writingTracks.includes(track.id));
  const volumeTarget: AutomationTarget = {
    kind: 'track',
    trackId: track.id,
    effectId: null,
    paramId: 'volume',
  };
  const panTarget: AutomationTarget = { kind: 'track', trackId: track.id, effectId: null, paramId: 'pan' };
  const project = useDaw((s) => s.project);
  const dispatch = useDaw((s) => s.dispatch);
  const heldBackSec = useDaw((s) => s.heldBackSec[track.id] ?? 0);

  const routingOptions = project.trackOrder
    .map((id) => project.tracks[id])
    .filter((t) => t && t.id !== track.id && (t.kind === 'bus' || t.kind === 'aux'));

  return (
    <div
      className={`channel ${master ? 'channel--master' : ''}`}
      style={selected ? { borderColor: 'var(--line-strong)' } : undefined}
      onPointerDown={() => useDaw.getState().setSelection({ trackId: track.id })}
    >
      <div className="channel__name" title={track.name} style={{ color: master ? 'var(--text-0)' : undefined }}>
        <span style={{ color: track.color }}>▌</span> {track.name}
      </div>
      {heldBackSec > 0.0005 && (
        <div
          className="channel__held"
          style={{ fontSize: 9, color: 'var(--text-2)', padding: '0 2px 2px' }}
          title={`Held back ${(heldBackSec * 1000).toFixed(1)} ms so it lines up with a plugin elsewhere that answers late. Everything arrives together, that much later.`}
        >
          +{(heldBackSec * 1000).toFixed(0)} ms
        </div>
      )}

      <div className="inserts">
        {track.effects.map((effect) => (
          <button
            key={effect.id}
            className={`chip ${effect.enabled ? 'chip--mon chip--on' : ''}`}
            style={{ width: '100%', justifyContent: 'space-between', marginBottom: 2, height: 17 }}
            onClick={() =>
              dispatch(
                command(EffectCommands.setEffectEnabled, {
                  trackId: track.id,
                  effectId: effect.id,
                  enabled: !effect.enabled,
                }),
              )
            }
            onContextMenu={(e) => {
              e.preventDefault();
              dispatch(command(EffectCommands.removeEffect, { trackId: track.id, effectId: effect.id }));
            }}
            title={`${effectDescriptor(effect).label} — click to bypass, right-click to remove`}
          >
            {effectDescriptor(effect).label}
          </button>
        ))}
        <select
          className="select"
          style={{ width: '100%', height: 18, fontSize: 10 }}
          value=""
          aria-label={`Add an effect to ${track.name}`}
          onChange={(e) => {
            const type = e.target.value;
            e.target.value = '';
            if (!type) return;
            if (type === 'wam') {
              // A plugin is fetched from wherever its author put it, so the one
              // thing to ask for is where.
              const url = window.prompt('Web Audio Module URL — the address of the plugin module (https://…/index.js)');
              if (url) useDaw.getState().addWebAudioModule(track.id, url);
              return;
            }
            dispatch({ type: EffectCommands.addEffect.type, params: { trackId: track.id, type } });
          }}
        >
          <option value="">+ Insert</option>
          {Object.values(EFFECT_DESCRIPTORS).map((d) => (
            <option key={d.type} value={d.type}>
              {d.type === 'wam' ? `${d.label}…` : d.label}
            </option>
          ))}
        </select>
      </div>

      {!master && (
        <div className="sends" aria-label={`Sends from ${track.name}`}>
          {track.sends.map((send) => {
            const target = project.tracks[send.targetTrackId];
            const name = target?.name ?? '?';
            return (
              <div key={send.id} className="send" title={`Send to ${name}`}>
                <div className="send__head">
                  <button
                    className={`chip ${send.enabled ? 'chip--mon chip--on' : ''}`}
                    style={{ flex: 1, justifyContent: 'flex-start', minWidth: 0, height: 16 }}
                    aria-pressed={send.enabled}
                    aria-label={`Send from ${track.name} to ${name}`}
                    title={`${send.enabled ? 'Mute' : 'Enable'} the send to ${name}; right-click to remove it`}
                    onClick={() =>
                      dispatch(command(TrackCommands.setSendEnabled, { trackId: track.id, sendId: send.id, enabled: !send.enabled }))
                    }
                    onContextMenu={(e) => {
                      e.preventDefault();
                      dispatch(command(TrackCommands.removeSend, { trackId: track.id, sendId: send.id }));
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>→ {name}</span>
                  </button>
                  <button
                    className={`chip ${send.preFader ? 'chip--on' : ''}`}
                    style={{ width: 30, height: 16, padding: 0, justifyContent: 'center' }}
                    aria-pressed={send.preFader}
                    aria-label={`Send from ${track.name} to ${name} pre-fader`}
                    title={
                      send.preFader
                        ? 'Pre-fader: taken after the inserts and before the fader, so the fader does not move it. Click for post-fader.'
                        : 'Post-fader: follows the fader and the pan. Click for pre-fader.'
                    }
                    onClick={() =>
                      dispatch(command(TrackCommands.setSendPreFader, { trackId: track.id, sendId: send.id, preFader: !send.preFader }))
                    }
                  >
                    {send.preFader ? 'PRE' : 'POST'}
                  </button>
                </div>
                <input
                  className="slider"
                  type="range"
                  min={0}
                  max={1.5}
                  step={0.01}
                  value={send.gain}
                  aria-label={`Send level from ${track.name} to ${name}`}
                  onChange={(e) =>
                    dispatch(command(TrackCommands.setSendGain, { trackId: track.id, sendId: send.id, gain: Number(e.target.value) }))
                  }
                  onPointerUp={() => getServices().commands.breakCoalescing()}
                />
              </div>
            );
          })}
          {routingOptions.some((t) => !track.sends.some((s) => s.targetTrackId === t.id)) && (
            <select
              className="select"
              style={{ width: '100%', height: 18, fontSize: 10 }}
              value=""
              aria-label={`Add a send from ${track.name}`}
              onChange={(e) => {
                if (!e.target.value) return;
                dispatch({ type: TrackCommands.addSend.type, params: { trackId: track.id, targetTrackId: e.target.value, gain: 0.5 } });
                e.target.value = '';
              }}
            >
              <option value="">+ Send</option>
              {routingOptions
                .filter((t) => !track.sends.some((s) => s.targetTrackId === t.id))
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    → {t.name}
                  </option>
                ))}
            </select>
          )}
        </div>
      )}

      <div className="channel__row">
        <input
          className="slider"
          type="range"
          min={-1}
          max={1}
          step={0.01}
          value={track.pan}
          aria-label={`${track.name} pan`}
          onPointerDown={() => useDaw.getState().touchParameter(panTarget)}
          onChange={(e) => {
            const pan = Number(e.target.value);
            dispatch(command(TrackCommands.setTrackPan, { trackId: track.id, pan }));
            // Pan runs from -1 to 1; a lane runs from 0 to 1.
            useDaw.getState().writeParameter(panTarget, (pan + 1) / 2);
          }}
          onPointerUp={() => {
            useDaw.getState().releaseParameter(panTarget);
            getServices().commands.breakCoalescing();
          }}
        />
      </div>
      <div className="channel__value">{formatPan(track.pan)}</div>

      <div className="channel__fader-row">
        <input
          className="channel__fader"
          type="range"
          min={0}
          max={1.6}
          step={0.001}
          value={track.volume}
          aria-label={`${track.name} fader`}
          onPointerDown={() => useDaw.getState().touchParameter(volumeTarget)}
          onChange={(e) => {
            const volume = Number(e.target.value);
            dispatch(command(TrackCommands.setTrackVolume, { trackId: track.id, volume }));
            useDaw.getState().writeParameter(volumeTarget, volume / 4);
          }}
          onPointerUp={() => {
            useDaw.getState().releaseParameter(volumeTarget);
            getServices().commands.breakCoalescing();
          }}
        />
        <Meter trackId={track.id} label={track.name} />
      </div>
      <div className="channel__value">{formatDb(track.volume)} dB</div>
      <StripLoudnessReadout trackId={track.id} />

      <div className="channel__row">
        <select
          className={`numeric channel__mode ${writing ? 'channel__mode--writing' : ''}`}
          value={track.automationMode}
          aria-label={`Automation mode for ${track.name}`}
          title="Read plays the lanes. Touch writes while a control is held, latch until the transport stops, write from the moment it rolls."
          onChange={(e) =>
            dispatch(
              command(TrackCommands.setAutomationMode, {
                trackId: track.id,
                mode: e.target.value as Track['automationMode'],
              }),
            )
          }
        >
          <option value="read">Read</option>
          <option value="touch">Touch</option>
          <option value="latch">Latch</option>
          <option value="write">Write</option>
        </select>
      </div>
      {!master && (
        <button
          className="btn btn--ghost channel__match"
          aria-label={`Match ${track.name} to -18 LUFS`}
          title="Render this track alone, measure it, and set its fader so it sits at -18 LUFS."
          disabled={matching}
          onClick={() => void useDaw.getState().matchTrackLoudness(track.id, -18)}
        >
          {matching ? '…' : 'Match -18'}
        </button>
      )}

      <div className="channel__row">
        <button
          className={`chip chip--mute ${track.mute ? 'chip--on' : ''}`}
          onClick={() => dispatch(command(TrackCommands.setTrackMute, { trackId: track.id, mute: !track.mute }))}
          aria-pressed={track.mute}
        >
          M
        </button>
        {!master && (
          <button
            className={`chip chip--solo ${track.solo ? 'chip--on' : ''}`}
            onClick={(e) =>
              dispatch(command(TrackCommands.setTrackSolo, { trackId: track.id, solo: !track.solo, exclusive: !e.altKey }))
            }
            aria-pressed={track.solo}
          >
            S
          </button>
        )}
      </div>

      {!master && (
        <select
          className="select"
          style={{ height: 18, fontSize: 10 }}
          value={track.outputTrackId ?? project.masterTrackId}
          aria-label={`${track.name} output`}
          onChange={(e) => dispatch(command(TrackCommands.setTrackOutput, { trackId: track.id, outputTrackId: e.target.value }))}
        >
          <option value={project.masterTrackId}>→ Master</option>
          {routingOptions.map((t) => (
            <option key={t.id} value={t.id}>
              → {t.name}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/**
 * Momentary loudness for one strip, in LUFS.
 *
 * Polled rather than pushed through React state, and only while the transport is
 * running: this is a number that changes ten times a second on forty channels,
 * and routing that through a store would re-render the whole mixer for it. Four
 * updates a second is enough to balance by — the eye cannot use more, and a
 * figure that flickers is one nobody reads.
 */
function StripLoudnessReadout({ trackId }: { trackId: string }): JSX.Element {
  const transportState = useDaw((s) => s.transportState);
  const [lufs, setLufs] = useState<number | null>(null);

  useEffect(() => {
    if (transportState === 'stopped') {
      setLufs(null);
      return;
    }
    const id = setInterval(() => {
      const reading = getServices().engine.readStripLoudness().get(trackId);
      setLufs(reading !== undefined && Number.isFinite(reading) ? reading : null);
    }, 250);
    return () => clearInterval(id);
  }, [trackId, transportState]);

  return (
    <div className="channel__lufs" aria-label={`Loudness of ${trackId}`}>
      {lufs === null ? '—' : `${lufs.toFixed(1)} LUFS`}
    </div>
  );
}
